import React from 'react';
import { useTranslation } from 'react-i18next';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import { formatActivityDate } from '@/lib/dashboardData';
import {
  Activity,
  ClipboardList,
  UserPlus,
  UserCheck,
  FileSignature,
} from 'lucide-react';

const kindMeta = {
  'job-note': {
    Icon: ClipboardList,
    labelKey: 'dashboard.activity_feed.badge_job',
    badgeClass:
      'bg-[hsl(var(--primary))]/12 text-[hsl(var(--primary))] border-[hsl(var(--primary))]/25',
    dotClass: 'bg-[hsl(var(--primary))]/80',
  },
  'client-touch': {
    Icon: UserCheck,
    labelKey: 'dashboard.activity_feed.badge_client',
    badgeClass:
      'bg-[hsl(var(--info))]/12 text-[hsl(var(--info))] border-[hsl(var(--info))]/25',
    dotClass: 'bg-[hsl(var(--info))]/80',
  },
  'lead-touch': {
    Icon: UserPlus,
    labelKey: 'dashboard.activity_feed.badge_lead',
    badgeClass:
      'bg-[hsl(var(--warning))]/12 text-[hsl(var(--warning))] border-[hsl(var(--warning))]/25',
    dotClass: 'bg-[hsl(var(--warning))]/80',
  },
  'proposal-signed': {
    Icon: FileSignature,
    labelKey: 'dashboard.activity_feed.badge_signed',
    badgeClass:
      'bg-[hsl(var(--success))]/15 text-[hsl(var(--success))] border-[hsl(var(--success))]/30',
    dotClass: 'bg-[hsl(var(--success))]',
  },
};

const FeedItem = ({ item }) => {
  const { t } = useTranslation();
  const meta = kindMeta[item.kind] || kindMeta['job-note'];
  const { Icon } = meta;
  const dateStr = formatActivityDate(item.date) || item.rawDate || '';

  return (
    <li
      className="relative pl-8 pr-2 py-2.5 group"
      data-testid={`activity-item-${item.id}`}
    >
      {/* Vertical rail */}
      <span
        aria-hidden
        className="absolute left-3 top-0 bottom-0 w-px bg-border group-first:top-3 group-last:bottom-auto group-last:h-3"
      />
      {/* Dot */}
      <span
        aria-hidden
        className={`absolute left-[10px] top-3 size-1.5 rounded-full ring-2 ring-[hsl(var(--card))] ${meta.dotClass || 'bg-[hsl(var(--primary))]/80'}`}
      />

      <div className="flex items-start gap-3">
        <div className="shrink-0 size-7 rounded-md border border-border bg-[hsl(var(--muted))] flex items-center justify-center text-[hsl(var(--primary))]">
          <Icon size={14} strokeWidth={2} />
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <Badge
              variant="outline"
              className={`uppercase text-[10px] tracking-[0.16em] ${meta.badgeClass}`}
            >
              {t(meta.labelKey)}
            </Badge>
            <span className="text-sm font-semibold text-foreground truncate">
              {item.title}
            </span>
            {dateStr ? (
              <span className="ml-auto text-[11px] uppercase tracking-[0.18em] text-muted-foreground shrink-0">
                {dateStr}
              </span>
            ) : null}
          </div>
          {item.subtitle ? (
            <div className="mt-0.5 text-xs text-muted-foreground truncate">
              {item.subtitle}
            </div>
          ) : null}
          {item.text ? (
            <p className="mt-1.5 text-sm text-foreground/90 leading-snug line-clamp-3">
              {item.text}
            </p>
          ) : null}
        </div>
      </div>
    </li>
  );
};

export const ActivityFeed = ({ items = [] }) => {
  const { t } = useTranslation();
  return (
    <Card
      className="border-[hsl(var(--hairline))] bg-[hsl(var(--card))] h-full"
      data-testid="activity-feed"
    >
      <CardContent className="p-4 sm:p-5">
        <div className="flex items-center justify-between mb-2">
          <div>
            <div className="text-[10px] font-medium uppercase tracking-[0.22em] text-muted-foreground">
              {t('dashboard.activity')}
            </div>
            <h2 className="mt-0.5 text-base sm:text-lg font-semibold text-foreground">
              {t('dashboard.recent_updates')}
            </h2>
          </div>
          <Activity size={16} className="text-[hsl(var(--primary))]/70" />
        </div>

        {items.length === 0 ? (
          <div className="py-10 text-center text-sm text-muted-foreground">
            {t('dashboard.no_activity')}
          </div>
        ) : (
          <ScrollArea className="max-h-[480px] kc-scroll pr-2">
            <ul className="divide-y divide-border/60">
              {items.map((item, idx) => (
                <FeedItem key={`${item.id}-${idx}`} item={item} />
              ))}
            </ul>
          </ScrollArea>
        )}
      </CardContent>
    </Card>
  );
};

export default ActivityFeed;
